import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { authorized, buildFeatureFlagChecker } from "common/authorization";
import { SUPPORTED_LANGUAGES } from "common/constants";
import { accountSupportsElevationCertificates } from "common/utils/documentTypes";
import { ExtendedApolloClient } from "../../apollo";
import { useGetGuestAccountDetailsQuery } from "../../generated/graphql";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useSecurityConfiguration } from "../../hooks/useSecurityConfiguration";
import { getBrowserLanguage } from "../../utils/browserLanguage";
import {
  SELECTED_LANGUAGE_KEY,
  updateSelectedLanguage,
} from "../../utils/i18n/utils";
import { setTitle } from "../../utils/title";
import Icon, { ICON_COLORS, Icons } from "../Common/Icons";
import UnavailablePage from "../Guest/UnavailablePage";
import { MainRouteProps } from "../types";
import { LoadingContainer } from "./__styles__/GuestAuthContext";
import { AuthContext } from "./AuthContext";
import { AuthContextType, CurrentAdmin } from "./types";
import { trackEntity, untrackEntity } from "./utils";

export type GuestAuthContextType = AuthContextType;

const GuestAuthContextProvider = ({
  children,
  publicUser,
  loadingAuthorizedEntity,
  refetchAuthorizedEntity,
}: PropsWithChildren<MainRouteProps>) => {
  const browserLanguage = getBrowserLanguage({ stripRegion: true });
  const { t, i18n } = useTranslation();
  const { getValue, setValue } = useLocalStorage<SUPPORTED_LANGUAGES>({
    key: SELECTED_LANGUAGE_KEY,
  });
  const client = useApolloClient() as ExtendedApolloClient;

  const { securityConfiguration, loading: loadingSecurityConfiguration } =
    useSecurityConfiguration({
      entity: publicUser,
    });

  const {
    data,
    loading: loadingGuestAccountDetails,
    error,
  } = useGetGuestAccountDetailsQuery({
    onCompleted: data => {
      const supportedLanguages =
        data.account?.publicPortal.supportedLanguages || [];

      const defaultLanguage = SUPPORTED_LANGUAGES.ENGLISH;
      const setLanguage = getValue();

      const selectedLanguage = setLanguage || browserLanguage;
      const nonDefaultLanguageSelected =
        selectedLanguage !== defaultLanguage &&
        supportedLanguages.includes(selectedLanguage as SUPPORTED_LANGUAGES);
      const languageChanged = i18n.language !== selectedLanguage;

      if (selectedLanguage && nonDefaultLanguageSelected && languageChanged) {
        void updateSelectedLanguage({
          language: selectedLanguage as SUPPORTED_LANGUAGES,
          updateStaticTranslations: i18n.changeLanguage,
          updateDynamicTranslations: client.updateLanguage,
          updateLocalStorage: setValue,
        });
      }
    },
  });

  useEffect(() => {
    if (publicUser) {
      trackEntity(publicUser);
    } else {
      untrackEntity();
    }
  }, [publicUser]);

  const account = useMemo(() => {
    if (!data?.account) return null;

    return {
      ...data?.account,
      securityConfiguration: {
        ...data?.account?.securityConfiguration,
        ...securityConfiguration,
      },
    };
  }, [data?.account, securityConfiguration]);

  if (
    loadingAuthorizedEntity ||
    loadingSecurityConfiguration ||
    loadingGuestAccountDetails
  ) {
    setTitle(t("common-loading-state"));
    return (
      <LoadingContainer data-testid="guest-auth-loading-container">
        <Icon icon={Icons.LOADING} color={ICON_COLORS.LIGHT_GREY} />
      </LoadingContainer>
    );
  }

  if (error || !account) return null;

  const supportsElevationCertificates =
    accountSupportsElevationCertificates(account);

  const featureFlags = account?.featureFlags || [];

  return account.publicPortal.enabled ?
      <AuthContext.Provider
        value={{
          account,
          user: null,
          admin: null,
          publicUser: publicUser ?? null,
          loadingAuthorizedEntity: loadingAuthorizedEntity ?? false,
          isPublic: true,
          setAdmin: (_admin: CurrentAdmin) => {},
          isFeatureEnabled: buildFeatureFlagChecker(featureFlags),
          authorized: authDetails =>
            authorized({
              entity: {
                role: { name: publicUser ? "publicUser" : "guest" },
                canViewPersonalIdentifiableInformation: false,
              },
              ...authDetails,
            }),
          supportsElevationCertificates,
          refetchAuthorizedEntity: refetchAuthorizedEntity ?? (() => {}),
        }}
      >
        {children}
      </AuthContext.Provider>
    : <UnavailablePage />;
};

export default GuestAuthContextProvider;
